import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Container,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { TimeoutsLeftIndicator } from "./TimeoutsLeftIndicator";
import SportsFootballIcon from "@mui/icons-material/SportsFootball";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PlaylistAddCheckSharpIcon from "@mui/icons-material/PlaylistAddCheckSharp";
import BarChartIcon from "@mui/icons-material/BarChart";
import { StartGame } from "./StartGame";
import { TabConfig } from "../../../types/GeneralTypes";
import { AllSideTabs } from "../../../constants";
import OffenseGameLogger from "./Offense/OffenseGameLogger";
import KickOffReturnLogger from "./KickOffReturn/KickOffReturnLogger";
import { useLogger } from "../../../contexts/LoggerContext";
import { LogTable } from "./LogTable";
import { Side } from "../../../types/DepthChartTypes";
import { InGameStats } from "./InGameStats";
import { SavePlayDialog } from "./SavePlayDialog";
import FieldGoalLogger from "./FieldGoal/FieldGoalLogger";

export const GameLogger = () => {
  const {
    gameStarted,
    selectedTab,
    selectedSide,
    selectedGame,
    gameSituation,
    setSelectedTab,
    setSelectedSide,
  } = useLogger();

  const [showLogTable, setShowLogTable] = useState(false);
  const [showInGameStats, setShowInGameStats] = useState(false);
  const [clearSelections, setClearSelections] = useState(false);
  const [savePlayDialogOpen, setSavePlayDialogOpen] = useState(false);

  const clubName = selectedGame?.user_club?.name.replace(/ School$/, "");
  const oppClubName = selectedGame?.opponent_club?.name.replace(/ School$/, "");
  const clubLogo = selectedGame?.user_club?.profile_picture;
  const oppClubLogo = selectedGame?.opponent_club?.profile_picture;
  const clubMascot = selectedGame?.user_club?.mascot;
  const oppClubMascot = selectedGame?.opponent_club?.mascot;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setSelectedSide(AllSideTabs[newValue].value as Side);
  };

  const handleLogTableToggle = () => {
    if (showInGameStats) {
      setShowInGameStats(false);
    }
    setShowLogTable(!showLogTable);
  };

  const handleInGameStatsToggle = () => {
    if (showLogTable) {
      setShowLogTable(false);
    }
    setShowInGameStats(!showInGameStats);
  };

  return (
    <Container
      sx={{
        mt: 2,
        mb: 4,
        maxWidth: 1275,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 1,
          borderRadius: 2,
          minHeight: "calc(100vh - 150px)",
          width: "100%",
        }}
      >
        {/* Game Logger */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Scoreboard */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              px: { xs: 1, sm: 2 },
              py: 1,
              gap: { xs: 1, sm: 2 },
              borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            }}
          >
            {/* User Club Logo and Name (side by side) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 120,
                gap: 1,
              }}
            >
              {clubLogo && (
                <Box
                  component="img"
                  src={clubLogo}
                  alt={clubName}
                  sx={{
                    height: 40,
                    width: 40,
                    objectFit: "contain",
                    borderRadius: "4px",
                    ml: { xs: 0, sm: 0.5 },
                    mr: 0.5,
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  ml: 0.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {clubName}
                </Typography>
                {clubMascot && (
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ whiteSpace: "nowrap", lineHeight: 1.1 }}
                  >
                    {clubMascot}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* User Club Timeouts (vertical) and Score (full height) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 32,
                mr: 0.0,
                gap: 0.5,
              }}
            >
              <TimeoutsLeftIndicator
                timeoutsLeft={gameSituation.club_tol}
                size={8}
              />
              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{
                  lineHeight: 1,
                  minWidth: 28,
                  textAlign: "center",
                  mr: 0.25,
                }}
              >
                {gameSituation.club_score}
              </Typography>
              {/* Football icon if user club has possession */}
              {gameSituation.club_possession && (
                <SportsFootballIcon
                  sx={{
                    color: "gold",
                    fontSize: 20,
                    ml: 0.25,
                    mr: 0,
                    verticalAlign: "middle",
                  }}
                />
              )}
            </Box>

            {/* VS */}
            <Typography variant="h6" sx={{ color: "grey.400" }}>
              VS
            </Typography>

            {/* Opponent Club Score (full height) and Timeouts (vertical) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 32,
                ml: 0.0,
                gap: 0.5,
              }}
            >
              {/* Football icon if opponent club has possession */}
              {!gameSituation.club_possession && (
                <SportsFootballIcon
                  sx={{
                    color: "gold",
                    fontSize: 20,
                    mr: 0.5,
                    ml: 0,
                    verticalAlign: "middle",
                  }}
                />
              )}
              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{ lineHeight: 1, minWidth: 28, textAlign: "center" }}
              >
                {gameSituation.opp_score}
              </Typography>
              <TimeoutsLeftIndicator
                timeoutsLeft={gameSituation.opp_tol}
                size={8}
              />
            </Box>

            {/* Opponent Club Logo and Name (side by side) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 120,
                gap: 1,
                pr: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mr: 0.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {oppClubName}
                </Typography>
                {oppClubMascot && (
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ whiteSpace: "nowrap", lineHeight: 1.1 }}
                  >
                    {oppClubMascot}
                  </Typography>
                )}
              </Box>
              {oppClubLogo && (
                <Box
                  component="img"
                  src={oppClubLogo}
                  alt={oppClubName}
                  sx={{
                    height: 40,
                    width: 40,
                    objectFit: "contain",
                    borderRadius: "4px",
                    mr: 0,
                    ml: 0.5,
                  }}
                />
              )}
            </Box>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />

            {/* Game Situation Info: Single row, each stat is a column with label and value stacked */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 4,
                minWidth: 420,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1">Qtr</Typography>
                <Typography variant="h6" fontWeight={500}>
                  {gameSituation.quarter === -1 ? "-" : gameSituation.quarter}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1">Time</Typography>
                <Typography variant="h6" fontWeight={500}>
                  {gameSituation.time_remaining}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", minWidth: 60 }}
                >
                  Ball On
                </Typography>
                <Typography variant="h6" fontWeight={500}>
                  {gameSituation.yardline}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1">Down</Typography>
                <Typography variant="h6" fontWeight={500}>
                  {gameSituation.down === -1 ? "-" : gameSituation.down}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1">Distance</Typography>
                <Typography variant="h6" fontWeight={500}>
                  {gameSituation.distance === -1 ? "-" : gameSituation.distance}
                </Typography>
              </Box>
              {/* Far right vertical divider and stacked icons */}
              <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  minWidth: 32,
                }}
              >
                <Tooltip title="Game Logs">
                  <IconButton onClick={handleLogTableToggle}>
                    <AssignmentIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="In-Game Stats">
                  <IconButton onClick={handleInGameStatsToggle}>
                    <BarChartIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          {/* Main Content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            {/* Game Started Logic */}
            {gameStarted ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                  flexDirection: "column",
                  width: "100%",
                  flex: 1,
                }}
              >
                {/* Sides Tabs and Save Play Log Button */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width: "100%",
                    flex: 1,
                  }}
                >
                  {/* Sides Tabs */}
                  <Box sx={{ flex: 1 }}>
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        "& .MuiTabs-indicator": {
                          backgroundColor: "rgba(123, 45, 58, 0.9)",
                        },
                        "& .MuiTab-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                          "&.Mui-selected": {
                            color: "white",
                          },
                        },
                      }}
                    >
                      {AllSideTabs.map((tab: TabConfig, index: number) => (
                        <Tab
                          key={tab.value}
                          label={tab.label}
                          id={`game-logger-tab-${index}`}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {/* Save Play Log Button */}
                  <Box sx={{ justifyContent: "flex-end", mr: 1 }}>
                    <Tooltip title="Save Play Log">
                      <IconButton onClick={() => setSavePlayDialogOpen(true)}>
                        <PlaylistAddCheckSharpIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Game Logging Content */}
                {selectedSide === "OFFENSE" && (
                  <OffenseGameLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
                {selectedSide === "DEFENSE" && (
                  <OffenseGameLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
                {selectedSide === "KICKOFF" && <KickOffReturnLogger />}
                {selectedSide === "KICKOFF_RETURN" && <KickOffReturnLogger />}
                {selectedSide === "PUNT" && (
                  <OffenseGameLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
                {selectedSide === "PUNT_RETURN" && (
                  <OffenseGameLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
                {selectedSide === "FIELD_GOAL" && (
                  <FieldGoalLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
                {selectedSide === "FIELD_GOAL_BLOCK" && (
                  <OffenseGameLogger
                    clearSelections={clearSelections}
                    setClearSelections={setClearSelections}
                  />
                )}
              </Box>
            ) : (
              <StartGame />
            )}
            <LogTable open={showLogTable} />
            <InGameStats open={showInGameStats} />
            <SavePlayDialog
              open={savePlayDialogOpen}
              onClose={() => setSavePlayDialogOpen(false)}
              setClearSelections={setClearSelections}
            />
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};


